import { editorUrl } from '@/lib/editorBasePath';
import { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import { YearbookPage, YearbookElement, PageBackground } from '@/types/yearbook';
import { normalizeElements } from '@/lib/normalizeElements';
import { toast } from 'sonner';

const DEBOUNCE_MS = 1000;
const LOAD_RADIUS = 3;

interface ApiPageResponse {
  id?: number;
  page_id: number;
  item_type?: string;
  named?: string | null;
  note?: string | null;
  objects_positions: {
    elements: YearbookElement[];
    background: PageBackground;
    guides?: import('@/types/yearbook').PageGuide[];
  };
}

function parseApiPages(raw: ApiPageResponse[]): YearbookPage[] {
  return raw.map((item) => {
    try {
      return {
        id: String(item.id ?? item.page_id),
        pageNumber: item.page_id,
        elements: normalizeElements(item.objects_positions?.elements ?? []),
        background: item.objects_positions?.background ?? { type: 'color' as const, value: '#ffffff' },
        guides: item.objects_positions?.guides ?? [],
        name: item.named || null,
        noteToGraphiste: item.note || undefined,
      };
    } catch {
      // Fallback fake page
      return {
        id: String(item.page_id ?? Math.random().toString(36).slice(2)),
        pageNumber: item.page_id ?? 0,
        elements: [],
        background: { type: 'color' as const, value: '#ffffff' },
      };
    }
  });
}

export function useAutoSave(
  pages: YearbookPage[],
  mergePages: (merger: (prev: YearbookPage[]) => YearbookPage[]) => void,
  currentPageIndex: number,
  preloadedIndices?: Set<number>,
  lockedPageNumbers?: Set<number>
) {
  const lockedRef = useRef<Set<number>>(lockedPageNumbers ?? new Set());
  useEffect(() => { lockedRef.current = lockedPageNumbers ?? new Set(); }, [lockedPageNumbers]);
  const prevPagesRef = useRef<string>('');
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isInitRef = useRef(false);
  const pagesRef = useRef(pages);
  useEffect(() => { pagesRef.current = pages; }, [pages]);
  const loadedIndicesRef = useRef<Set<number>>(new Set());
  const loadingRef = useRef<Set<number>>(new Set());
  /** Pages that were successfully loaded from the server at least once.
   *  Only these are eligible to be saved back. Failed loads NEVER end up here. */
  const serverLoadedRef = useRef<Set<number>>(new Set());
  /** Page IDs that are explicitly allowed to be saved as empty (e.g. user reset).
   *  Bypasses the "non-empty -> empty" guard. Cleared after the save runs. */
  const allowEmptyOnceRef = useRef<Set<string>>(new Set());

  const [loadedIndices, setLoadedIndices] = useState<Set<number>>(new Set());
  const [activeSaves, setActiveSaves] = useState(0);
  const [saveError, setSaveError] = useState<string | null>(null);
  const isSaving = useMemo(() => activeSaves > 0, [activeSaves]);

  // Seed with preloaded indices on first render
  const seededRef = useRef(false);
  if (!seededRef.current && preloadedIndices && preloadedIndices.size > 0) {
    seededRef.current = true;
    preloadedIndices.forEach((i) => {
      loadedIndicesRef.current.add(i);
      serverLoadedRef.current.add(i);
    });
    setLoadedIndices(new Set(loadedIndicesRef.current));
    isInitRef.current = true;
    prevPagesRef.current = JSON.stringify(pages);
  }

  /** Detect which page changed and POST it */
  const saveModifiedPage = useCallback((currentPages: YearbookPage[]) => {
    const prevSnapshot = prevPagesRef.current;
    const currentSnapshot = JSON.stringify(currentPages);

    if (prevSnapshot === currentSnapshot) return;

    let prevPages: YearbookPage[] = [];
    try {
      prevPages = prevSnapshot ? JSON.parse(prevSnapshot) : [];
    } catch {
      prevPages = [];
    }

    for (let i = 0; i < currentPages.length; i++) {
      // Only save pages that have been successfully loaded from the server.
      // This prevents overwriting real server data with empty local skeletons
      // when a load_pages_ids fetch failed (returned HTML, 502, etc.).
      if (!serverLoadedRef.current.has(i)) continue;

      // Skip locked pages: they must never be persisted.
      if (lockedRef.current.has(currentPages[i].pageNumber)) continue;

      const currentStr = JSON.stringify(currentPages[i]);
      const prevStr = prevPages[i] ? JSON.stringify(prevPages[i]) : '';
      if (currentStr !== prevStr) {
        const page = currentPages[i];
        const prevPage = prevPages[i];

        // Safety net: never silently overwrite a non-empty page with an empty one.
        // If something went wrong upstream and the local state lost its elements,
        // skip the save and log loudly so we can investigate.
        const prevHadElements = Array.isArray(prevPage?.elements) && prevPage.elements.length > 0;
        const nowEmpty = !page.elements || page.elements.length === 0;
        const isAllowedEmpty = allowEmptyOnceRef.current.has(String(page.id));
        if (prevHadElements && nowEmpty && !isAllowedEmpty) {
          console.warn(
            '[AutoSave] BLOCKED empty save for page',
            page.id,
            '(pageNumber:', page.pageNumber, ') — previous snapshot had',
            prevPage.elements.length,
            'elements. Likely a load failure or race. Skipping POST.'
          );
          continue;
        }
        if (isAllowedEmpty) {
          allowEmptyOnceRef.current.delete(String(page.id));
        }

        const itemType = page.itemType || (page.coverType === 'tranche' ? 'tranche' : page.coverType ? 'cover' : 'page');
        setActiveSaves((n) => n + 1);
        fetch(editorUrl('/save_page'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: page.id,
            page_id: page.pageNumber,
            item_type: itemType,
            objects_positions: {
              elements: page.elements.map(({ forbiddenWords, ...rest }: any) => {
                const userId = rest.userId ?? rest.user_id;
                // Always persist the trombi student id in both cases so the
                // backend and the loader can match elements reliably.
                return userId !== undefined && userId !== null
                  ? { ...rest, userId, user_id: userId }
                  : rest;
              }),
              background: page.background,
              layout_template_id: page.layoutTemplateId ?? null,
              guides: page.guides ?? [],
            },
          }),
        })
          .then((res) => {
            if (res.redirected || res.type === 'opaqueredirect') {
              throw new Error(`Redirected (${res.status})`);
            }
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            // If the server responds with HTML (e.g. login page after session expiry), treat as error
            const ct = res.headers.get('content-type') || '';
            if (ct.includes('text/html')) {
              throw new Error('Unexpected HTML response');
            }
          })
          .catch((err) => {
            console.warn('[AutoSave] Failed to save page:', err);
            const msg = "La sauvegarde s'est mal passée. Veuillez recharger la page.";
            setSaveError(msg);
            toast.error(msg, {
              id: 'save-error',
              duration: Infinity,
              action: {
                label: 'Recharger la page',
                onClick: () => window.location.reload(),
              },
            });
          })
          .finally(() => setActiveSaves((n) => Math.max(0, n - 1)));
      }
    }

    prevPagesRef.current = currentSnapshot;
  }, []);

  /** Debounced save on every pages change */
  useEffect(() => {
    if (!isInitRef.current) return;

    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      saveModifiedPage(pages);
    }, DEBOUNCE_MS);

    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [pages, saveModifiedPage]);

  /** Load nearby pages when currentPageIndex changes */
  const loadNearbyPages = useCallback(
    (centerIndex: number) => {
      const totalPages = pages.length;
      const indicesToLoad: number[] = [];

      for (
        let i = Math.max(0, centerIndex - LOAD_RADIUS);
        i <= Math.min(totalPages - 1, centerIndex + LOAD_RADIUS);
        i++
      ) {
        if (!loadedIndicesRef.current.has(i) && !loadingRef.current.has(i)) {
          indicesToLoad.push(i);
        }
      }

      if (indicesToLoad.length === 0) return;

      loadPagesByIndices(indicesToLoad);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pages.length]
  );

  /** Load specific page indices. Returns a promise that resolves when done. */
  const loadPagesByIndices = useCallback(
    (indicesToLoad: number[]): Promise<void> => {
      if (indicesToLoad.length === 0) return Promise.resolve();

      // Mark as loading
      indicesToLoad.forEach((i) => loadingRef.current.add(i));

      // Send page ids as comma-separated list
      const pageIds = indicesToLoad.map((i) => pages[i].id);

      return fetch(editorUrl(`/load_pages_ids?pages_ids=${pageIds.join(',')}`))
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.json();
        })
        .then((rawData: ApiPageResponse[]) => {
          if (!Array.isArray(rawData)) return;

          const parsed = parseApiPages(rawData);

          mergePages((currentPages) => {
            const updated = currentPages.map((page) => {
              const loaded = parsed.find((d) => d.pageNumber === page.pageNumber);
              return loaded || page;
            });
            // Capture the snapshot synchronously here, BEFORE any subsequent
            // setState (e.g. setPageContent right after a trombi creation) can
            // mutate pagesRef. Doing this in a [loadedIndicesSize] useEffect
            // races with those updates and produces an empty diff -> no save.
            if (isInitRef.current) {
              prevPagesRef.current = JSON.stringify(updated);
            }
            return updated;
          });


          // Only mark as server-loaded the indices that actually came back in the response.
          const returnedPageNumbers = new Set(parsed.map((p) => p.pageNumber));
          indicesToLoad.forEach((i) => {
            loadingRef.current.delete(i);
            const pageNum = pages[i]?.pageNumber;
            if (pageNum !== undefined && returnedPageNumbers.has(pageNum)) {
              loadedIndicesRef.current.add(i);
              serverLoadedRef.current.add(i);
            }
          });

          setLoadedIndices(new Set(loadedIndicesRef.current));

          if (!isInitRef.current && serverLoadedRef.current.size > 0) {
            isInitRef.current = true;
            prevPagesRef.current = JSON.stringify(pagesRef.current);
          }

          console.log('[AutoSave] Loaded pages:', pageIds);
        })
        .catch((err) => {
          // CRITICAL: do NOT overwrite local state with empty pages on failure,
          // and do NOT mark these indices as loaded. Otherwise the autosave
          // would later POST elements: [] and wipe real server data.
          console.warn('[AutoSave] Failed to load pages (will retry on next navigation):', err);
          indicesToLoad.forEach((i) => {
            loadingRef.current.delete(i);
          });
          // Do not flip isInitRef — keep autosave disabled until at least one
          // successful load happens, so we never save from an unknown baseline.
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pages.length]
  );

  /** Load ALL unloaded pages (used for preview/overview modes) */
  const loadAllPages = useCallback(() => {
    const indicesToLoad: number[] = [];
    for (let i = 0; i < pages.length; i++) {
      if (!loadedIndicesRef.current.has(i) && !loadingRef.current.has(i)) {
        indicesToLoad.push(i);
      }
    }
    if (indicesToLoad.length > 0) {
      loadPagesByIndices(indicesToLoad);
    }
  }, [pages.length, loadPagesByIndices]);

  // Initial load + load on page change
  useEffect(() => {
    loadNearbyPages(currentPageIndex);
  }, [currentPageIndex, loadNearbyPages]);

  // NOTE: snapshot re-sync after a lazy-load is performed synchronously inside
  // the mergePages updater above (see loadPagesByIndices). Doing it in a
  // [loadedIndicesSize] useEffect raced with subsequent setState calls (e.g.
  // setPageContent right after a trombi creation), capturing the post-mutation
  // state and producing an empty diff -> no autosave POST.


  /** Force-sync the snapshot so the next save cycle sees no diff.
   *  Pass explicit pages to capture state that hasn't been committed yet
   *  (e.g. from inside a setPages updater). Otherwise reads from pagesRef. */
  const syncSnapshot = useCallback((explicitPages?: YearbookPage[]) => {
    prevPagesRef.current = JSON.stringify(explicitPages ?? pagesRef.current);
  }, []);

  /** Inject a guide into the reference snapshot only, so the autosave diff does
   *  NOT see it as a local change (the guide is persisted server-side in bulk).
   *  Other concurrent edits are still detected normally. */
  const injectGuideInSnapshot = useCallback(
    (
      guide: import('@/types/yearbook').PageGuide,
      eligiblePageIds: Set<string>
    ) => {
      let prev: YearbookPage[] = [];
      try {
        prev = prevPagesRef.current ? JSON.parse(prevPagesRef.current) : [];
      } catch {
        return;
      }
      if (!Array.isArray(prev) || prev.length === 0) return;
      const next = prev.map((page) => {
        if (!eligiblePageIds.has(page.id)) return page;
        const guides = page.guides ?? [];
        if (guides.some((g) => g.id === guide.id)) return page;
        return { ...page, guides: [...guides, guide] };
      });
      prevPagesRef.current = JSON.stringify(next);
    },
    []
  );



  /** Flush: cancel debounce and save immediately */
  const flushSave = useCallback(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = null;
    // Use latest pages from ref to catch state that just committed
    setTimeout(() => saveModifiedPage(pagesRef.current), 0);
  }, [saveModifiedPage]);

  /** Ensure specific indices are loaded before proceeding */
  const ensureIndicesLoaded = useCallback(
    (indices: number[]): Promise<void> => {
      const toLoad = indices.filter(
        (i) => !loadedIndicesRef.current.has(i) && !loadingRef.current.has(i)
      );
      return loadPagesByIndices(toLoad);
    },
    [loadPagesByIndices]
  );

  /** Mark page IDs as explicitly allowed to save an empty elements array once
   *  (e.g. user reset). Without this the empty-save guard would block them. */
  const allowEmptySaveFor = useCallback((pageIds: string[]) => {
    pageIds.forEach((id) => allowEmptyOnceRef.current.add(String(id)));
  }, []);

  return { loadedIndices, syncSnapshot, injectGuideInSnapshot, flushSave, loadAllPages, ensureIndicesLoaded, isSaving, allowEmptySaveFor, saveError };
}
